<template>
  <el-drawer
    :model-value="getShowNotificationDrawer"
    @update:model-value="modifyShowNotificationDrawer"
    @open="reload"
    title="Notifications"
    direction="rtl"
    size="380px"
    append-to-body
  >
    <div class="notification-drawer-actions">
      <el-radio-group v-model="filter" size="small" @change="reload">
        <el-radio-button value="all">All</el-radio-button>
        <el-radio-button value="unread">
          Unread{{ getUnreadNotifications ? ` (${getUnreadNotifications})` : '' }}
        </el-radio-button>
      </el-radio-group>
      <el-button
        size="small"
        text
        :disabled="!getUnreadNotifications"
        @click="markAllRead"
      >
        Mark all read
      </el-button>
    </div>

    <div v-loading="loading && !items.length" class="notification-drawer-list">
      <div
        v-for="n in items"
        :key="n.id"
        class="notification-drawer-item"
        :class="{ 'notification-drawer-unread': !n.read_at }"
        @click="openNotification(n)"
      >
        <div class="notification-drawer-subject">
          <span v-if="!n.read_at" class="notification-drawer-dot"></span>
          {{ n.subject }}
        </div>
        <div v-if="n.message" class="notification-drawer-message">
          {{ n.message }}
        </div>
        <div class="notification-drawer-meta">
          {{ fromNow(n.created_at) }}
        </div>
      </div>

      <div v-if="!loading && !items.length" class="notification-drawer-empty">
        {{ filter == 'unread' ? 'No unread notifications' : 'No notifications yet' }}
      </div>

      <el-button
        v-if="hasMore"
        class="notification-drawer-more"
        size="small"
        :loading="loading"
        @click="loadMore"
      >
        Load more
      </el-button>
    </div>
  </el-drawer>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import { processError } from 'src/util/processError';
import userNotificationService from 'src/services/userNotificationService.js';

const PAGE_SIZE = 30;

export default {
  name: 'NotificationDrawer',
  data() {
    return {
      items: [],
      filter: 'all', // all | unread
      loading: false,
      hasMore: false,
    };
  },
  computed: {
    ...mapGetters('realtime', [
      'getShowNotificationDrawer',
      'getUnreadNotifications',
    ]),
  },
  watch: {
    // A live alert arrived while open, show it at the top
    getUnreadNotifications(n, o) {
      if (this.getShowNotificationDrawer && n > o) {
        this.reload();
      }
    },
  },
  methods: {
    ...mapActions('realtime', [
      'modifyShowNotificationDrawer',
      'modifyUnreadNotifications',
      'adjustUnreadNotifications',
    ]),
    fromNow(date) {
      return date ? moment(date).fromNow() : '';
    },
    async reload() {
      this.items = [];
      this.hasMore = false;
      await this.loadMore();
    },
    async loadMore() {
      this.loading = true;
      const beforeId = this.items.length
        ? this.items[this.items.length - 1].id
        : undefined;
      try {
        const page = await userNotificationService.list(
          beforeId,
          PAGE_SIZE,
          this.filter == 'unread'
        );
        this.items = this.items.concat(page);
        this.hasMore = page.length == PAGE_SIZE;
      } catch (e) {
        this.showError('Unable to load notifications', e);
      }
      this.loading = false;
    },
    async openNotification(n) {
      if (!n.read_at) {
        try {
          const updated = await userNotificationService.markRead(n.id);
          n.read_at = new Date().toISOString();
          if (updated) {
            this.adjustUnreadNotifications(-1);
          }
        } catch (e) {
          this.showError('Unable to mark notification read', e);
        }
      }
      const path = this.pathFor(n);
      if (path) {
        this.modifyShowNotificationDrawer(false);
        this.$router.push(path);
      }
    },
    // Where a click goes, by what the notification is about
    pathFor(n) {
      switch (n.related_type) {
        case 'conversation':
          return {
            path: '/admin/conversations/' + n.related_id,
            // Scroll to the mentioned note
            query: n.timeline_id
              ? { mentionTimeline: n.timeline_id }
              : undefined,
          };
        default:
          return undefined;
      }
    },
    async markAllRead() {
      try {
        await userNotificationService.markAllRead();
      } catch (e) {
        this.showError('Unable to mark notifications read', e);
        return;
      }
      const now = new Date().toISOString();
      this.items.forEach((n) => (n.read_at = n.read_at ?? now));
      this.modifyUnreadNotifications(0);
      if (this.filter == 'unread') {
        this.items = [];
        this.hasMore = false;
      }
    },
    showError(message, e) {
      this.$message({
        type: 'error',
        message: message + ': ' + processError(e.response ?? e),
        showClose: true,
      });
    },
  },
};
</script>

<style scoped>
.notification-drawer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.notification-drawer-item {
  padding: 10px 8px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  cursor: pointer;
}
.notification-drawer-item:hover {
  background: var(--el-fill-color-light);
}
.notification-drawer-unread .notification-drawer-subject {
  font-weight: 600;
}
.notification-drawer-subject {
  display: flex;
  align-items: center;
  gap: 6px;
}
.notification-drawer-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--el-color-primary);
  flex-shrink: 0;
}
.notification-drawer-message {
  margin-top: 2px;
  font-size: 13px;
  color: var(--el-text-color-regular);
  word-break: break-word;
}
.notification-drawer-meta {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.notification-drawer-empty {
  padding: 24px 0;
  text-align: center;
  color: var(--el-text-color-secondary);
}
.notification-drawer-more {
  display: block;
  margin: 12px auto 0;
}
</style>
